import { useState, useCallback } from 'react';
import type { TimeFrame } from 'components/AnimatedTimelineControl';

export interface PlaybackState {
  frames: TimeFrame[];
  currentFrameIndex: number;
  isPlaying: boolean;
}

export interface UsePlaybackStateResult extends PlaybackState {
  isLoading: boolean;
  setFrames: (frames: TimeFrame[]) => void;
  setIsPlaying: (playing: boolean) => void;
  jumpToFrame: (index: number, stopPlaying?: boolean) => void;
  togglePlayPause: () => void;
  stopAndReset: (frameIndex?: number) => void;
}

/**
 * Hook for managing playback state (frames, current index, playing status).
 */
export const usePlaybackState = (): UsePlaybackStateResult => {
  const [state, setState] = useState<PlaybackState>({
    frames: [],
    currentFrameIndex: 0,
    isPlaying: false,
  });

  const setFrames = useCallback(
    (frames: TimeFrame[]) => setState((prev) => ({ ...prev, frames })),
    [],
  );

  const setIsPlaying = useCallback(
    (isPlaying: boolean) => setState((prev) => ({ ...prev, isPlaying })),
    [],
  );

  const jumpToFrame = useCallback(
    (index: number, stopPlaying?: boolean) =>
      setState((prev) => ({
        ...prev,
        currentFrameIndex: index,
        ...(stopPlaying ? { isPlaying: false } : {}),
      })),
    [],
  );

  const togglePlayPause = useCallback(
    () => setState((prev) => ({ ...prev, isPlaying: !prev.isPlaying })),
    [],
  );

  const stopAndReset = useCallback(
    (frameIndex?: number) =>
      setState((prev) => ({
        ...prev,
        currentFrameIndex: frameIndex ?? prev.currentFrameIndex,
        isPlaying: false,
      })),
    [],
  );

  return {
    ...state,
    isLoading: state.frames.length === 0,
    setFrames,
    setIsPlaying,
    jumpToFrame,
    togglePlayPause,
    stopAndReset,
  };
};
